import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  api,
  formatMs,
  formatUsd,
  type ProviderView,
  type RequestDetail,
  type RequestRow,
} from "../lib/api";
import {
  Drawer,
  DrawerSection,
  EmptyState,
  ErrorState,
  FilterBar,
  PageHeader,
  ProviderMark,
  SkeletonRows,
  StatusBadge,
  TraceStep,
  toneForStatus,
} from "../components/ui";

type StatusPill = "all" | "success" | "error" | "fallback" | "free";

const PAGE_SIZE = 50;

export default function RequestsPage() {
  const [items, setItems] = useState<RequestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [providers, setProviders] = useState<ProviderView[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<RequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [pill, setPill] = useState<StatusPill>("all");
  const drawerOpen = useRef(false);
  drawerOpen.current = selected != null;

  const providerParam = searchParams.get("provider") ?? "";
  const modelParam = searchParams.get("model") ?? "";
  const idParam = searchParams.get("id") ?? "";

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      qs.set("limit", String(PAGE_SIZE));
      qs.set("offset", String(page * PAGE_SIZE));
      if (pill === "success" || pill === "error") qs.set("status", pill);
      if (pill === "fallback") qs.set("fallback", "1");
      if (pill === "free") qs.set("free", "1");
      if (providerParam) {
        const pv = providers.find((p) => p.providerTypeId === providerParam);
        if (pv) qs.set("provider", pv.id);
      }
      if (modelParam) qs.set("model", modelParam);
      const res = await api<{ data: RequestRow[]; total: number }>(`/api/requests?${qs}`);
      setItems(res.data);
      setTotal(res.total ?? res.data.length);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [pill, providerParam, modelParam, providers, page]);

  // Reset to first page whenever the server-side filters change.
  useEffect(() => {
    setPage(0);
  }, [pill, providerParam, modelParam]);

  useEffect(() => {
    void api<{ data: ProviderView[] }>("/api/providers")
      .then((r) => setProviders(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      if (!drawerOpen.current) void load();
    }, 8000);
    return () => clearInterval(t);
  }, [load]);

  // Deep link: /requests?id=<id>
  useEffect(() => {
    if (idParam && !selected) void openRequest(idParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam]);

  async function openRequest(id: string) {
    try {
      const detail = await api<RequestDetail>(`/api/requests/${id}`);
      setSelected(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load request");
    }
  }

  const filtered = useMemo(() => {
    let list = items;
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.requestedModel.toLowerCase().includes(q) ||
          r.resolvedModelId?.toLowerCase().includes(q) ||
          r.id.toLowerCase().includes(q) ||
          r.routingExplanation?.selected?.providerDisplayName?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [items, pill, query]);

  const providerFor = (r: RequestRow) =>
    providers.find((p) => p.id === r.resolvedProviderId);

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <PageHeader
        title="Requests"
        subtitle="Request explorer with routing decisions, attempts, and fallbacks."
        right={<span className="text-xs text-canvas-muted">{total} total</span>}
      />

      <FilterBar
        query={query}
        onQuery={setQuery}
        placeholder="Search model, request id, provider…"
        pills={[
          { id: "all", label: "All", count: total },
          { id: "success", label: "Success" },
          { id: "error", label: "Errors" },
          { id: "fallback", label: "Fallbacks" },
          { id: "free", label: "Free" },
        ]}
        activePill={pill}
        onPill={(id) => setPill(id as StatusPill)}
        right={
          <>
            <select
              value={providerParam}
              onChange={(e) =>
                setSearchParams(
                  (p) => {
                    const n = new URLSearchParams(p);
                    if (e.target.value) n.set("provider", e.target.value);
                    else n.delete("provider");
                    return n;
                  },
                  { replace: true },
                )
              }
              className="rounded-lg border border-canvas-border bg-canvas-raised px-2.5 py-1.5 text-xs text-canvas-muted outline-none focus:border-accent"
              aria-label="Provider"
            >
              <option value="">All providers</option>
              {providers.map((p) => (
                <option key={p.id} value={p.providerTypeId}>{p.name}</option>
              ))}
            </select>
            {(modelParam || providerParam || idParam) && (
              <button
                className="btn-ghost !py-1.5 text-xs"
                onClick={() => setSearchParams({}, { replace: true })}
              >
                Clear filters
              </button>
            )}
          </>
        }
      />

      {modelParam && (
        <p className="mb-3 text-xs text-canvas-muted">
          Filtered to model <span className="mono text-canvas-ink">{modelParam}</span>
        </p>
      )}
      {error && <div className="mb-4"><ErrorState message={error} onRetry={load} /></div>}

      {loading ? (
        <SkeletonRows rows={8} height={48} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No requests"
          body={
            pill === "all" && !query && !modelParam && !providerParam
              ? "Send traffic to the OpenAI-compatible gateway — every request lands here with its full routing trace."
              : "No requests match these filters."
          }
        />
      ) : (
        <div className="panel overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-canvas-border">
                <th className="table-th">Time</th>
                <th className="table-th">Model</th>
                <th className="table-th">Provider</th>
                <th className="table-th">Status</th>
                <th className="table-th">Latency</th>
                <th className="table-th">Tokens</th>
                <th className="table-th">Cost</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-canvas-border">
              {filtered.map((r) => {
                const pv = providerFor(r);
                return (
                  <tr
                    key={r.id}
                    className="cursor-pointer transition hover:bg-canvas-raised"
                    onClick={() => void openRequest(r.id)}
                  >
                    <td className="table-td mono whitespace-nowrap text-canvas-muted">
                      {new Date(r.createdAt).toLocaleTimeString()}
                    </td>
                    <td className="table-td">
                      <div className="font-medium">{r.requestedModel}</div>
                      {r.routingExplanation?.selected?.displayName &&
                        r.routingExplanation.selected.displayName !== r.requestedModel && (
                          <div className="text-[11px] text-canvas-muted">
                            → {r.routingExplanation.selected.displayName}
                          </div>
                        )}
                    </td>
                    <td className="table-td">
                      <div className="flex items-center gap-2">
                        {pv && (
                          <ProviderMark
                            letter={pv.definition.branding.letter}
                            accent={pv.definition.branding.accent}
                            size={20}
                          />
                        )}
                        <span className="text-xs">
                          {pv?.name ?? r.routingExplanation?.selected?.providerDisplayName ?? "—"}
                        </span>
                        {pv?.providerTypeId.startsWith("mock-") && (
                          <span className="chip-muted !text-[10px]">DEMO</span>
                        )}
                      </div>
                    </td>
                    <td className="table-td">
                      <StatusBadge label={r.status} tone={toneForStatus(r.status)} />
                      {r.fallbackCount > 0 && (
                        <span className="chip-warn ml-1 !text-[10px]">fb×{r.fallbackCount}</span>
                      )}
                    </td>
                    <td className="table-td mono whitespace-nowrap">{formatMs(r.latencyMs)}</td>
                    <td className="table-td mono text-canvas-muted">
                      {r.inputTokens ?? "—"}/{r.outputTokens ?? "—"}
                    </td>
                    <td className="table-td mono">
                      {r.freeTier ? <span className="text-accent">free</span> : formatUsd(r.costUsd)}
                    </td>
                    <td className="table-td text-right text-xs text-canvas-muted">→</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-canvas-border px-4 py-2.5">
            <span className="text-xs text-canvas-muted">
              {total === 0
                ? "0 requests"
                : `Showing ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
            </span>
            <div className="flex gap-1.5">
              <button
                className="btn-ghost !px-2.5 !py-1 text-xs disabled:opacity-40"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← Prev
              </button>
              <button
                className="btn-ghost !px-2.5 !py-1 text-xs disabled:opacity-40"
                disabled={(page + 1) * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <RequestTrace
          detail={selected}
          providers={providers}
          onClose={() => {
            setSelected(null);
            if (idParam)
              setSearchParams(
                (p) => {
                  const n = new URLSearchParams(p);
                  n.delete("id");
                  return n;
                },
                { replace: true },
              );
          }}
        />
      )}
    </div>
  );
}

/* ── Request trace drawer ──────────────────────────────────────── */

function RequestTrace({
  detail: r,
  providers,
  onClose,
}: {
  detail: RequestDetail;
  providers: ProviderView[];
  onClose: () => void;
}) {
  const ex = r.routingExplanation;
  const pv = providers.find((p) => p.id === r.resolvedProviderId);

  return (
    <Drawer
      title={
        <span className="flex items-center gap-2">
          <StatusBadge label={r.status} tone={toneForStatus(r.status)} />
          <span className="mono text-xs font-normal text-canvas-muted">{r.id}</span>
        </span>
      }
      onClose={onClose}
      wide
    >
      <dl className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {(
          [
            ["Latency", formatMs(r.latencyMs)],
            ["TTFT", formatMs(r.ttftMs)],
            ["Tokens", `${r.inputTokens ?? "—"} in / ${r.outputTokens ?? "—"} out`],
            ["Cost", r.freeTier ? "Free" : formatUsd(r.costUsd)],
            ["Fallbacks", String(r.fallbackCount)],
            ["Retries", String(r.retryCount)],
          ] as const
        ).map(([k, v]) => (
          <div key={k} className="rounded-lg border border-canvas-border bg-canvas px-3 py-2">
            <dt className="text-[10px] uppercase tracking-wider text-canvas-muted">{k}</dt>
            <dd className="mono mt-0.5 text-sm">{v}</dd>
          </div>
        ))}
      </dl>

      <DrawerSection title="Routing trace">
        <ol>
          <TraceStep
            index={1}
            title="Request received"
            detail={
              <>
                <span className="mono">{r.endpoint}</span> · model{" "}
                <span className="mono">{r.requestedModel}</span> ·{" "}
                {new Date(r.createdAt).toLocaleTimeString()}
                {r.streaming && <span className="chip-muted ml-1 !text-[10px]">stream</span>}
              </>
            }
            tone="ok"
          />
          <TraceStep
            index={2}
            title="Route selected"
            detail={
              ex?.selected
                ? `${ex.selected.providerDisplayName} / ${ex.selected.displayName}`
                : "no routing decision recorded"
            }
            tone={ex?.selected ? "ok" : "warn"}
          />
          {r.attempts.map((a, i) => {
            const av = providers.find((p) => p.id === a.providerId);
            const ok = a.status === "success";
            return (
              <TraceStep
                key={a.attemptNumber}
                index={3 + i}
                title={
                  ok
                    ? `${i === 0 ? "Primary" : `Fallback #${i}`} succeeded`
                    : `${i === 0 ? "Primary" : `Fallback #${i}`} failed`
                }
                detail={
                  <>
                    <span className="font-medium text-canvas-ink">
                      {av?.name ?? a.providerTypeId}
                    </span>
                    {" / "}
                    <span className="mono">{a.providerModelId}</span>
                    {" · "}
                    {formatMs(a.latencyMs)}
                    {a.freeTier && <span className="chip-ok ml-1 !text-[10px]">FREE</span>}
                    {a.httpStatus != null && (
                      <span className="ml-1 text-canvas-muted">HTTP {a.httpStatus}</span>
                    )}
                    {a.errorCode && (
                      <div className="mt-0.5 text-danger">
                        {a.errorCode.replace(/_/g, " ")}
                        {a.errorMessage && (
                          <span className="text-canvas-muted"> — {a.errorMessage.slice(0, 160)}</span>
                        )}
                      </div>
                    )}
                  </>
                }
                tone={ok ? "ok" : "danger"}
                last={i === r.attempts.length - 1}
              />
            );
          })}
          <TraceStep
            index={3 + r.attempts.length}
            title={r.status === "success" ? "Response delivered" : "Request failed"}
            detail={
              r.status === "success"
                ? `${formatMs(r.latencyMs)} total · ${r.outputTokens ?? "—"} tokens`
                : (r.errorMessage ?? r.errorCode ?? "unknown error")
            }
            tone={r.status === "success" ? "ok" : "danger"}
            last
          />
        </ol>
      </DrawerSection>

      {ex?.selected?.reasons && ex.selected.reasons.length > 0 && (
        <DrawerSection title="Why this route">
          <ul className="space-y-1 rounded-xl border border-canvas-border bg-canvas p-3.5 text-sm">
            {ex.selected.reasons.map((reason) => (
              <li key={reason} className="flex gap-2">
                <span className="text-accent">✓</span>
                {reason}
              </li>
            ))}
          </ul>
        </DrawerSection>
      )}

      <DrawerSection title="Identifiers">
        <div className="space-y-1 rounded-xl border border-canvas-border bg-canvas p-3.5 text-[11px]">
          <p className="mono text-canvas-muted">request: {r.id}</p>
          <p className="mono text-canvas-muted">trace: {r.traceId}</p>
          {pv && <p className="mono text-canvas-muted">provider: {pv.providerTypeId}</p>}
          {r.resolvedModelId && (
            <p className="mono text-canvas-muted">model: {r.resolvedModelId}</p>
          )}
        </div>
      </DrawerSection>
    </Drawer>
  );
}
